import React, { useState } from 'react';
import { Search, CheckCircle, XCircle, Shield, Download, Globe, Award, Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { generateCertificatePDF } from '../utils/generateCertificatePDF';

const CertificateVerificationPage = () => {
  const { t } = useLanguage();
  const [certificateCode, setCertificateCode] = useState('');
  const [verificationResult, setVerificationResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Simulated certificate database
  const mockCertificates = {
    'CO2N-2024-001': {
      valid: true,
      company: 'TechCorp GmbH',
      website: 'www.techcorp.de',
      issuedDate: '2024-01-15',
      expiryDate: '2025-01-15',
      carbonOffset: '2.5 ton CO₂',
      certificateId: 'CO2N-2024-001',
      status: t('cert-page-status-active'),
      verificationCount: 1247
    },
    'CO2N-2024-002': {
      valid: true,
      company: 'GreenStart Solutions',
      website: 'www.greenstart.com',
      issuedDate: '2024-02-20',
      expiryDate: '2025-02-20',
      carbonOffset: '1.8 ton CO₂',
      certificateId: 'CO2N-2024-002',
      status: t('cert-page-status-active'),
      verificationCount: 892
    },
    'CO2N-2024-003': {
      valid: true,
      company: 'EcoTech Industries',
      website: 'www.ecotech.com',
      issuedDate: '2024-03-10',
      expiryDate: '2025-03-10',
      carbonOffset: '3.2 ton CO₂',
      certificateId: 'CO2N-2024-003',
      status: t('cert-page-status-active'),
      verificationCount: 567
    },
    'CO2N-2026-001905': {
      valid: true,
      company: 'truja.de',
      website: 'truja.de',
      issuedDate: '02.03.2024',
      expiryDate: '02.03.2027',
      carbonOffset: t('cert-truja-carbon-offset'),
      certificateId: 'CO2N-2026-001905',
      status: t('cert-page-status-active'),
      verificationCount: 1850
    },
    '555270000666-#748999': {
      valid: true,
      company: 'Neckar Paletten',
      website: 'neckar-paletten.de',
      issuedDate: '2025-01-02',
      expiryDate: '2026-01-02',
      carbonOffset: t('cert-neckar-carbon-offset'),
      certificateId: '555270000666-#748999',
      status: t('cert-page-status-active'),
      verificationCount: 1250
    },
    'CO2N-2025-ISH-001': {
      valid: true,
      company: 'Insektenschutzhelden',
      website: 'insektenschutzhelden.de',
      issuedDate: '18.03.2025',
      expiryDate: '18.03.2026',
      carbonOffset: t('cert-ish-carbon-offset'),
      certificateId: 'CO2N-2025-ISH-001',
      status: t('cert-page-status-active'),
      verificationCount: 342,
      logoUrl: 'https://www.insektenschutzhelden.de/assets/logo-D796bX3J.png'
    },
    'CO2N-2025-FLY24-ENCZ': {
      valid: true,
      company: 'Enucuzfly',
      website: 'enucuzfly.com',
      issuedDate: '15.11.2024',
      expiryDate: '15.11.2026',
      carbonOffset: t('cert-enucuzfly-carbon-offset'),
      certificateId: 'CO2N-2025-FLY24-ENCZ',
      status: t('cert-page-status-active'),
      verificationCount: 410
    },
    'INVALID-001': {
      valid: false,
      reason: t('cert-verification-not-found')
    }
  };

  const verifyCertificate = async () => {
    if (!certificateCode.trim()) {
      setVerificationResult({
        valid: false,
        reason: t('cert-verification-enter-code')
      });
      return;
    }

    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      const result = mockCertificates[certificateCode] || {
        valid: false,
        reason: t('cert-verification-invalid-code')
      };
      
      setVerificationResult(result);
      setIsLoading(false);
    }, 1500);
  };

  const downloadCertificate = async (certificateId) => {
    const certificate = mockCertificates[certificateId];
    if (certificate && certificate.valid) {
      await generateCertificatePDF(certificate, t);
    }
  };

  return (
    <section className="min-h-screen bg-neutral-50 py-12 pt-32">
      <div className="container-custom max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="bg-primary-100 p-4 rounded-full">
              <Shield className="h-12 w-12 text-primary-600" />
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-800 mb-6">
            {t('cert-page-title')}
          </h1>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto mb-8">
            {t('cert-page-subtitle')}
          </p>
          
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-2xl mx-auto">
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center justify-center mb-3">
                <Globe className="h-8 w-8 text-primary-500" />
              </div>
              <div className="text-2xl font-bold text-neutral-800">15K+</div>
              <div className="text-sm text-neutral-600">{t('cert-page-stats-verified')}</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center justify-center mb-3">
                <Award className="h-8 w-8 text-primary-500" />
              </div>
              <div className="text-2xl font-bold text-neutral-800">99.9%</div>
              <div className="text-sm text-neutral-600">{t('cert-page-stats-accuracy')}</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center justify-center mb-3">
                <Clock className="h-8 w-8 text-primary-500" />
              </div>
              <div className="text-2xl font-bold text-neutral-800">1.5s</div>
              <div className="text-sm text-neutral-600">{t('cert-page-stats-time')}</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Verification Area */}
          <div className="lg:col-span-2">
            {/* Verification Form */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-neutral-800 mb-6 flex items-center gap-2">
                <Search className="h-6 w-6 text-primary-500" />
                {t('cert-page-verify-title')}
              </h2>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder={t('cert-verification-placeholder')}
                    value={certificateCode}
                    onChange={(e) => setCertificateCode(e.target.value)}
                    className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-lg"
                    onKeyPress={(e) => e.key === 'Enter' && verifyCertificate()}
                  />
                </div>
                <button
                  onClick={verifyCertificate}
                  disabled={isLoading}
                  className="btn-primary flex items-center justify-center space-x-2 px-8 py-3 text-lg"
                >
                  {isLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <Search className="h-5 w-5" />
                  )}
                  <span>{isLoading ? t('cert-verification-verifying') : t('cert-verification-verify')}</span>
                </button>
              </div>
            </div>

            {/* Verification Result */}
            {verificationResult && (
              <div className={`rounded-2xl p-8 shadow-lg ${
                verificationResult.valid 
                  ? 'bg-green-50 border-2 border-green-200' 
                  : 'bg-red-50 border-2 border-red-200'
              }`}>
                <div className="flex items-center mb-6">
                  {verificationResult.valid ? (
                    <CheckCircle className="h-10 w-10 text-green-500 mr-4" />
                  ) : (
                    <XCircle className="h-10 w-10 text-red-500 mr-4" />
                  )}
                  <div>
                    <h3 className={`text-3xl font-bold ${
                      verificationResult.valid ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {verificationResult.valid ? t('cert-verification-valid') : t('cert-verification-invalid')}
                    </h3>
                    <p className={`text-lg ${
                      verificationResult.valid ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {verificationResult.valid ? t('cert-page-valid-message') : verificationResult.reason}
                    </p>
                  </div>
                </div>

                {verificationResult.valid && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-white rounded-lg p-4">
                        <label className="text-sm font-medium text-neutral-600">{t('cert-verification-company')}</label>
                        <p className="text-xl font-semibold text-neutral-800">{verificationResult.company}</p>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <label className="text-sm font-medium text-neutral-600">{t('cert-verification-website')}</label>
                        <p className="text-xl font-semibold text-primary-600">{verificationResult.website}</p>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <label className="text-sm font-medium text-neutral-600">{t('cert-verification-issued-date')}</label>
                        <p className="text-xl font-semibold text-neutral-800">{verificationResult.issuedDate}</p>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <label className="text-sm font-medium text-neutral-600">{t('cert-verification-expiry-date')}</label>
                        <p className="text-xl font-semibold text-neutral-800">{verificationResult.expiryDate}</p>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <label className="text-sm font-medium text-neutral-600">{t('cert-verification-carbon-offset')}</label>
                        <p className="text-xl font-semibold text-green-600">{verificationResult.carbonOffset}</p>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <label className="text-sm font-medium text-neutral-600">{t('cert-verification-certificate-id')}</label>
                        <p className="text-xl font-semibold text-neutral-800">{verificationResult.certificateId}</p>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <label className="text-sm font-medium text-neutral-600">{t('cert-page-status')}</label>
                        <p className="text-xl font-semibold text-green-600">{verificationResult.status}</p>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <label className="text-sm font-medium text-neutral-600">{t('cert-page-verification-count')}</label>
                        <p className="text-xl font-semibold text-neutral-800">{verificationResult.verificationCount.toLocaleString()}</p>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 pt-6">
                      <button
                        onClick={() => downloadCertificate(verificationResult.certificateId)}
                        className="btn-secondary flex items-center justify-center space-x-2"
                      >
                        <Download className="h-5 w-5" />
                        <span>{t('cert-verification-download-pdf')}</span>
                      </button>
                      <div className="flex items-center space-x-2 text-green-600">
                        <Shield className="h-5 w-5" />
                        <span className="text-sm">{t('cert-verification-blockchain-verified')}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Example Certificates */}
            <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-xl font-semibold text-neutral-800 mb-4">{t('cert-verification-example-codes')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-neutral-50 p-4 rounded-lg">
                  <p className="text-sm text-neutral-600 mb-2">{t('cert-verification-valid-cert')}:</p>
                  <p className="font-mono text-primary-600">CO2N-2024-001</p>
                </div>
                <div className="bg-neutral-50 p-4 rounded-lg">
                  <p className="text-sm text-neutral-600 mb-2">{t('cert-verification-valid-cert')}:</p>
                  <p className="font-mono text-primary-600">CO2N-2024-002</p>
                </div>
                <div className="bg-neutral-50 p-4 rounded-lg">
                  <p className="text-sm text-neutral-600 mb-2">{t('cert-verification-valid-cert')}:</p>
                  <p className="font-mono text-primary-600">CO2N-2024-003</p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Features */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="text-xl font-semibold text-neutral-800 mb-4">{t('cert-page-features-title')}</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-primary-500 mt-1" />
                  <div>
                    <h4 className="font-medium text-neutral-800">{t('cert-page-feature-1-title')}</h4>
                    <p className="text-sm text-neutral-600">{t('cert-page-feature-1-desc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Globe className="h-5 w-5 text-primary-500 mt-1" />
                  <div>
                    <h4 className="font-medium text-neutral-800">{t('cert-page-feature-2-title')}</h4>
                    <p className="text-sm text-neutral-600">{t('cert-page-feature-2-desc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Award className="h-5 w-5 text-primary-500 mt-1" />
                  <div>
                    <h4 className="font-medium text-neutral-800">{t('cert-page-feature-3-title')}</h4>
                    <p className="text-sm text-neutral-600">{t('cert-page-feature-3-desc')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Verifications */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="text-xl font-semibold text-neutral-800 mb-4">{t('cert-page-recent-title')}</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-neutral-50 rounded-lg">
                  <div>
                    <p className="font-medium text-neutral-800">CO2N-2024-001</p>
                    <p className="text-sm text-neutral-600">TechCorp GmbH</p>
                  </div>
                  <span className="text-sm text-green-600 font-medium">{t('cert-page-recent-verified')}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-neutral-50 rounded-lg">
                  <div>
                    <p className="font-medium text-neutral-800">CO2N-2024-002</p>
                    <p className="text-sm text-neutral-600">GreenStart Solutions</p>
                  </div>
                  <span className="text-sm text-green-600 font-medium">{t('cert-page-recent-verified')}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-neutral-50 rounded-lg">
                  <div>
                    <p className="font-medium text-neutral-800">CO2N-2024-003</p>
                    <p className="text-sm text-neutral-600">EcoTech Industries</p>
                  </div>
                  <span className="text-sm text-green-600 font-medium">{t('cert-page-recent-verified')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Client Logos Slider */}
        <div className="mt-16 -mx-4 sm:-mx-6 lg:-mx-8">
          <h3 className="text-xl font-semibold text-neutral-800 mb-6 text-center">{t('global-partners-title')}</h3>
          <div className="relative overflow-hidden bg-white py-8 shadow-lg">
            <div className="flex animate-scroll">
              {[
                { name: 'Google',    src: 'https://logo.clearbit.com/google.com' },
                { name: 'Microsoft', src: 'https://logo.clearbit.com/microsoft.com' },
                { name: 'Amazon',    src: 'https://logo.clearbit.com/amazon.com' },
                { name: 'Tesla',     src: 'https://logo.clearbit.com/tesla.com' },
                { name: 'Samsung',   src: 'https://logo.clearbit.com/samsung.com' },
                { name: 'Apple',     src: 'https://logo.clearbit.com/apple.com' },
                { name: 'IBM',       src: 'https://logo.clearbit.com/ibm.com' },
                { name: 'Siemens',   src: 'https://logo.clearbit.com/siemens.com' },
                /* duplicate for seamless loop */
                { name: 'Google',    src: 'https://logo.clearbit.com/google.com' },
                { name: 'Microsoft', src: 'https://logo.clearbit.com/microsoft.com' },
                { name: 'Amazon',    src: 'https://logo.clearbit.com/amazon.com' },
                { name: 'Tesla',     src: 'https://logo.clearbit.com/tesla.com' },
                { name: 'Samsung',   src: 'https://logo.clearbit.com/samsung.com' },
                { name: 'Apple',     src: 'https://logo.clearbit.com/apple.com' },
                { name: 'IBM',       src: 'https://logo.clearbit.com/ibm.com' },
                { name: 'Siemens',   src: 'https://logo.clearbit.com/siemens.com' },
              ].map((logo, i) => (
                <div key={i} className="flex items-center justify-center min-w-[180px] mx-6">
                  <img
                    src={logo.src}
                    alt={logo.name}
                    className="h-10 w-10 object-contain grayscale hover:grayscale-0 transition-all duration-300 rounded-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <span
                    style={{ display: 'none' }}
                    className="text-lg font-bold text-neutral-400"
                  >
                    {logo.name}
                  </span>
              </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CertificateVerificationPage; 